import React from 'react';
import PropTypes from 'prop-types';
import Select2 from '../Select2.jsx';
import FieldErrors, { fieldClass } from './FieldErrors.jsx';

function humanize(name) {
  return name.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// Stable reference so Select2 doesn't re-init on every parent render.
const SELECT2_OPTIONS = { minimumResultsForSearch: Infinity };

// Swatch + hex input, kept in lockstep on one value. Every color in the tab is
// edited this way, so the pair lives here once rather than being re-typed per color.
// stacked defaults in the signature, not via defaultProps — React 19 ignores
// defaultProps on function components.
function ColorRow({
  value, onChange, testId, stacked = false,
}) {
  const set = (e) => onChange(e.target.value);
  return (
    <div className={stacked ? 'qr-color-row qr-color-row--stacked' : 'qr-color-row'}>
      <input type="color" className="qr-color-swatch" value={value} onChange={set} />
      <input type="text" className="form-control" data-testid={testId} value={value} onChange={set} />
    </div>
  );
}

ColorRow.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string.isRequired,
  stacked: PropTypes.bool,
};

// Checkbox + label, the standalone toggle that heads each color section.
function Toggle({
  label, checked, onChange, testId,
}) {
  return (
    <label className="qr-check-row">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

Toggle.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string.isRequired,
};

class ColorsTab extends React.Component {
  constructor(props) {
    super(props);
    this.typeData = props.gradientTypes.map((t) => ({ id: t, text: humanize(t) }));
  }

  setStop(index, value) {
    // Merge into the live gradient (functional update) rather than a closure-captured
    // one — a stale Select2 change could otherwise overwrite the other stop.
    this.props.patchStyle('gradient', (g) => ({
      ...g, stops: g.stops.map((s, i) => (i === index ? value : s)),
    }));
  }

  render() {
    const {
      style, patchStyle, gradientTypes, errors,
    } = this.props;
    const gradientOn = !!style.gradient;
    const fg = style.foreground_color;
    // "Match dot color" is meaningless with a gradient — the generator paints eyes
    // as a solid color, never the gradient. So while a gradient is on we hide the
    // match toggle and always show a solid eye-color picker.
    const matchEye = !gradientOn && style.eye_color == null;
    const eyeColor = style.eye_color || fg;

    return (
      <div className="qr-colors-tab">
        {/* Each section: an option title with its toggle floated right on the same
            line, then the picker/controls stacked below. */}
        <div className={fieldClass(errors, ['foreground_color', 'gradient'])}>
          <div className="qr-field-head">
            <label className="qr-label">Dot fill</label>
            <Toggle
              label="Gradient"
              testId="gradient-toggle"
              checked={gradientOn}
              onChange={(on) => patchStyle('gradient', on ? { type: gradientTypes[0], stops: [fg, fg] } : null)}
            />
          </div>
          {!gradientOn && (
            <ColorRow
              value={style.foreground_color}
              testId="foreground-hex"
              onChange={(v) => patchStyle('foreground_color', v)}
            />
          )}
          <FieldErrors messages={errors.foreground_color} />
          {/* Gradient sub-controls expand below the toggle; the solid picker above
              is hidden while the gradient is on (its color is unused then). */}
          {gradientOn && (
            <div className="qr-gradient" data-testid="gradient-controls">
              <div className="qr-field">
                <label className="qr-label">Gradient type</label>
                <Select2
                  className="form-control"
                  data-testid="gradient-type"
                  value={style.gradient.type}
                  data={this.typeData}
                  options={SELECT2_OPTIONS}
                  onChange={(e) => {
                    // Functional update: merge the new type into live state so a stale
                    // Select2 change can't reset the (edited) stops. See setStop.
                    const { value } = e.target;
                    patchStyle('gradient', (g) => ({ ...g, type: value }));
                  }}
                />
              </div>
              <label className="qr-label">Gradient colors</label>
              {style.gradient.stops.map((stop, i) => (
                <ColorRow
                  // Index key is stable: Style pins the gradient to exactly two
                  // positional stops, so a row is never inserted or reordered.
                  key={i}
                  value={stop}
                  testId={`gradient-stop-${i}`}
                  stacked={i > 0}
                  onChange={(v) => this.setStop(i, v)}
                />
              ))}
              {/* Style validates the gradient as one unit (type + both stops), so its
                  errors key to :gradient and sit under the pair, not per stop. */}
              <FieldErrors messages={errors.gradient} />
            </div>
          )}
        </div>

        <div className={fieldClass(errors, 'eye_color')}>
          <div className="qr-field-head">
            <label className="qr-label">Eye color</label>
            {!gradientOn && (
              <Toggle
                label="Match dot color"
                testId="match-eye-color"
                checked={matchEye}
                onChange={(on) => patchStyle('eye_color', on ? null : style.foreground_color)}
              />
            )}
          </div>
          {(gradientOn || !matchEye) && (
            <ColorRow
              value={eyeColor}
              testId="eye-hex"
              onChange={(v) => patchStyle('eye_color', v)}
            />
          )}
          {/* Outside the row conditional: a rejected eye_color can come back as null,
              which hides the picker — the message must still be visible. */}
          <FieldErrors messages={errors.eye_color} />
        </div>

        <div className={fieldClass(errors, 'background_color')}>
          <div className="qr-field-head">
            <label className="qr-label">Background</label>
            <Toggle
              label="Transparent"
              testId="transparent-toggle"
              checked={!!style.transparency}
              onChange={(on) => patchStyle('transparency', on)}
            />
          </div>
          {!style.transparency && (
            <ColorRow
              value={style.background_color}
              testId="background-hex"
              onChange={(v) => patchStyle('background_color', v)}
            />
          )}
          <FieldErrors messages={errors.background_color} />
          {/* Transparency is a checkbox, so there is no .form-control to outline —
              the message alone carries it. */}
          <FieldErrors messages={errors.transparency} />
        </div>
      </div>
    );
  }
}

ColorsTab.propTypes = {
  style: PropTypes.object.isRequired,
  patchStyle: PropTypes.func.isRequired,
  gradientTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  errors: PropTypes.object,
};

ColorsTab.defaultProps = { errors: {} };

export default ColorsTab;
